import React, { useMemo, useState, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import { Link } from 'react-router-dom'
import * as XLSX from 'xlsx'

import IPButton from '../../../../../components/IPButton'
import Table from '../../../../../components/Table'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import IPInput from '../../../../../components/IPInput'
import InputCampaign from '../../../../../components/InputCampaign'
import IPCheckbox from '../../../../../components/IPCheckBox'
import MoneySmallDecimals from '../../../../../components/IPPriceDisplay/IPPriceDisplayNew'

import Translations from '../../../../../translations'
import { translate } from '../../../../../base/localization'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { useCustomReducer } from '../../../../../base/customHooks'
import { getDateByTimezoneOffset } from '../../../../../base/utils/dataHelper'

import { DealerDetails, MTReturnModel, QuotaListReturnModel, RequestModel, ReturnModel } from '../models/models'
import { GetFavProductDataApi } from '../services/getFavProductData'
import { IBrand, ICategoryList, ICompany } from '../../../components/models/models'
import { getBrandApi, getCategoryList, getCompanyApi } from '../../../components/api'
import { GetQuotaListApi } from '../services/getQuotaListApi'
import { GetMTListApi } from '../services/getMtListapi'

type TypeValue = 'Fav' | 'Recent' | 'B2BOrder' | 'AllOrder'

interface IFavProductState {
  companyListOptions: ISelectOption[]
  companyListOption?: ISelectOption

  typeOptions: ISelectOption[]
  sortOptions: ISelectOption[]
  typeOption?: ISelectOption

  brandList?: IBrand[]
  product?: string
  dealerCode?: string

  brandGroup?: boolean
  mtGroup?: boolean

  brand?: ISelectOption
  GroupList?: ISelectOption
  Kota?: ISelectOption
  Sort?: ISelectOption

  KotaList?: ISelectOption[]
  categoryList?: ISelectOption[]
  MTList?: ISelectOption[]
  MT?: ISelectOption
}

interface DailyMetric {
  Date: string // yyyy-MM-dd
  Currency?: string | null

  EventCount: number
  UniqueDealerCount: number
  UniqueUserCount: number
  UniqueProductCount: number

  OrderLineCount: number
  OrderCount: number
  Quantity: number
  Amount: number
  UnitPrice: number

  CampaignLine: number
  B2BOrderCount: number
  SAPOrderCount: number
}

interface FavProductStatsResponse {
  Items: ReturnModel[]
  Daily: DailyMetric[]
}

type OrderOverlayState =
  | { open: true; title: string; dealerCode: string; orderNumbers: string[] }
  | { open: false }

type SvgLine = { key: string; name: string }

function OrderOverlay(props: { state: OrderOverlayState; onClose: () => void }) {
  if (!props.state.open) return null
  const { title, dealerCode, orderNumbers } = props.state

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div onClick={props.onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />

      <div
        style={{
          position: 'relative',
          background: '#fff',
          minWidth: 520,
          maxWidth: '80vw',
          maxHeight: '80vh',
          overflow: 'auto',
          borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          padding: 20,
          zIndex: 1000000,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            Bayi Bazlı Ürün İstatistikler - {title} - {dealerCode}
          </div>
          <button
            type="button"
            onClick={props.onClose}
            style={{
              border: '1px solid #999',
              background: '#fff',
              padding: '6px 10px',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            KAPAT
          </button>
        </div>

        <div style={{ marginTop: 16, borderTop: '1px solid #ddd', paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, fontSize: 16 }}>
            <b style={{ width: 220, textAlign: 'center' }}>Sipariş Numarası</b>
          </div>

          {!orderNumbers || orderNumbers.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <b>Veri bulunamadı.</b>
            </div>
          ) : (
            orderNumbers.map((x, i) => (
              <div
                key={`${x}-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 8,
                  borderBottom: '1px solid #eee',
                  paddingBottom: 8,
                }}
              >
                <div style={{ width: 220, textAlign: 'center' }}>{x}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Interaktif SVG Line Chart:
 * - Tooltip: hover ile tarih + değerler
 * - Legend toggle: seri aç/kapat
 * - Hepsi / Hiçbiri
 */
function SimpleLineChart(props: { title: string; data: any[]; lines: SvgLine[] }) {
  const { title, data, lines } = props
  const [hidden, setHidden] = useState<Record<string, boolean>>({})
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  if (!data || data.length === 0) return null

  const width = 1000
  const height = 280
  const padL = 56
  const padR = 18
  const padT = 18
  const padB = 34

  const xMax = Math.max(0, data.length - 1)

  const activeLines = lines.filter(l => !hidden[l.key])
  const seriesForDomain = activeLines.length > 0 ? activeLines : lines

  let yMin = 0
  let yMax = 0
  for (let i = 0; i < data.length; i++) {
    const row = data[i]
    for (let j = 0; j < seriesForDomain.length; j++) {
      const v = Number(row?.[seriesForDomain[j].key] ?? 0)
      if (!Number.isFinite(v)) continue
      if (v < yMin) yMin = v
      if (v > yMax) yMax = v
    }
  }
  if (yMax === yMin) yMax = yMin + 1

  const xScale = (i: number) => padL + (xMax === 0 ? 0 : (i / xMax) * (width - padL - padR))
  const yScale = (v: number) => padT + (1 - (v - yMin) / (yMax - yMin)) * (height - padT - padB)

  const makePath = (key: string) => {
    let d = ''
    for (let i = 0; i < data.length; i++) {
      const vRaw = Number(data[i]?.[key] ?? 0)
      const v = Number.isFinite(vRaw) ? vRaw : 0
      const x = xScale(i)
      const y = yScale(v)
      d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`
    }
    return d
  }

  const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f']

  const fmt = (n: number) => {
    const abs = Math.abs(n)
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return `${Math.round(n * 100) / 100}`
  }

  const firstDate = data[0]?.Date ?? ''
  const lastDate = data[data.length - 1]?.Date ?? ''

  const getIdxFromMouse = (evt: React.MouseEvent<SVGRectElement, MouseEvent>) => {
    const r = evt.currentTarget.getBoundingClientRect()
    const x = evt.clientX - r.left // 0..r.width (plot alanı içinde)

    const w = r.width || 1
    const t = Math.min(1, Math.max(0, x / w)) // 0..1
    return Math.min(xMax, Math.max(0, Math.round(t * xMax)))
  }

  const hoverRow = hoverIdx != null ? data[hoverIdx] : null

  const toggleLine = (key: string) => setHidden(prev => ({ ...prev, [key]: !prev[key] }))
  const showAll = () => setHidden({})
  const hideAll = () => {
    const all: Record<string, boolean> = {}
    lines.forEach(l => (all[l.key] = true))
    setHidden(all)
  }

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={showAll}
            style={{ border: '1px solid #999', background: '#fff', padding: '4px 8px', borderRadius: 6 }}
          >
            Hepsi
          </button>
          <button
            type="button"
            onClick={hideAll}
            style={{ border: '1px solid #999', background: '#fff', padding: '4px 8px', borderRadius: 6 }}
          >
            Hiçbiri
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <svg width={width} height={height} style={{ display: 'block' }}>
          {/* axes */}
          <line x1={padL} y1={padT} x2={padL} y2={height - padB} stroke="#999" />
          <line x1={padL} y1={height - padB} x2={width - padR} y2={height - padB} stroke="#999" />

          {/* hover capture */}
          <rect
            x={padL}
            y={padT}
            width={width - padL - padR}
            height={height - padT - padB}
            fill="transparent"
            onMouseMove={e => setHoverIdx(getIdxFromMouse(e))}
            onMouseLeave={() => setHoverIdx(null)}
          />

          {/* x edge labels */}
          <text x={padL} y={height - 10} fontSize={11} textAnchor="start" fill="#444">
            {firstDate}
          </text>
          <text x={width - padR} y={height - 10} fontSize={11} textAnchor="end" fill="#444">
            {lastDate}
          </text>

          {/* series */}
          {lines.map((l, idx) => {
            const isHidden = !!hidden[l.key]
            if (isHidden) return null
            return (
              <path key={l.key} d={makePath(l.key)} fill="none" stroke={colors[idx % colors.length]} strokeWidth={2} />
            )
          })}

          {/* hover vline + points + tooltip */}
          {hoverRow && (
            <>
              <line x1={xScale(hoverIdx!)} y1={padT} x2={xScale(hoverIdx!)} y2={height - padB} stroke="#bbb" />

              {lines.map((l, idx) => {
                if (hidden[l.key]) return null
                const v = Number(hoverRow?.[l.key] ?? 0)
                const y = yScale(Number.isFinite(v) ? v : 0)
                const x = xScale(hoverIdx!)
                return <circle key={l.key} cx={x} cy={y} r={3} fill={colors[idx % colors.length]} />
              })}

              <g>
                {(() => {
                  const x = xScale(hoverIdx!)
                  const boxW = 280
                  const visible = lines.filter(l => !hidden[l.key]).slice(0, 10)
                  const boxH = 20 + 16 * (visible.length + 1)

                  const bx = Math.min(width - padR - boxW, Math.max(padL, x + 12))
                  const by = padT + 8

                  return (
                    <>
                      <rect x={bx} y={by} width={boxW} height={boxH} rx={8} ry={8} fill="#fff" stroke="#ddd" />
                      <text x={bx + 10} y={by + 16} fontSize={12} fill="#111">
                        <tspan style={{ fontWeight: 700 as any }}>{hoverRow.Date}</tspan>
                      </text>
                      {visible.map((l, i) => {
                        const v = Number(hoverRow?.[l.key] ?? 0)
                        return (
                          <text key={l.key} x={bx + 10} y={by + 16 + (i + 1) * 16} fontSize={12} fill="#333">
                            {l.name}: {fmt(Number.isFinite(v) ? v : 0)}
                          </text>
                        )
                      })}
                    </>
                  )
                })()}
              </g>
            </>
          )}
        </svg>
      </div>

      {/* legend clickable */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
        {lines.map((l, idx) => {
          const off = !!hidden[l.key]
          return (
            <button
              key={l.key}
              type="button"
              onClick={() => toggleLine(l.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                border: '1px solid #ddd',
                background: '#fff',
                padding: '4px 10px',
                borderRadius: 999,
                cursor: 'pointer',
                opacity: off ? 0.45 : 1,
              }}
              title={off ? 'Aç' : 'Kapat'}
            >
              <span style={{ width: 14, height: 3, background: colors[idx % colors.length], display: 'inline-block' }} />
              <span style={{ fontSize: 12 }}>{l.name}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Currency kırılımı varsa her currency için 3 chart basan blok
 */
function DailyChartsBlock(props: { currencyKey: string; data: any[] }) {
  const { currencyKey, data } = props
  const titleCur = currencyKey === 'NO_CURRENCY' ? '' : ` - ${currencyKey}`

  return (
    <div style={{ marginBottom: 16 }}>
      <SimpleLineChart
        title={`Günlük Sayımlar${titleCur}`}
        data={data}
        lines={[
          { key: 'EventCount', name: 'Olay' },
          { key: 'UniqueDealerCount', name: 'Tekil Bayi' },
          { key: 'UniqueUserCount', name: 'Tekil Kullanıcı' },
          { key: 'UniqueProductCount', name: 'Tekil Ürün' },
          { key: 'OrderCount', name: 'Sipariş' },
        ]}
      />

      <SimpleLineChart
        title={`Günlük Adet/Kampanya${titleCur}`}
        data={data}
        lines={[
          { key: 'Quantity', name: 'Adet' },
          { key: 'CampaignLine', name: 'Kampanya Satırı' },
          { key: 'OrderLineCount', name: 'Sipariş Satırı' },
          { key: 'B2BOrderCount', name: 'B2B Sipariş' },
          { key: 'SAPOrderCount', name: 'SAP Sipariş' },
        ]}
      />

      <SimpleLineChart
        title={`Günlük Tutar/Birim Fiyat${titleCur}`}
        data={data}
        lines={[
          { key: 'Amount', name: 'Toplam Tutar' },
          { key: 'UnitPrice', name: 'Birim Fiyat' },
        ]}
      />
    </div>
  )
}

function Index(props: BaseComponentWithContextProps) {
  const t = translate(Translations)

  const getInitialState = (): IFavProductState => ({
    companyListOptions: [],
    typeOptions: [
      { label: 'Favoriler', value: 'Fav' },
      { label: 'İnceleme', value: 'Recent' },
      { label: 'B2B Siparişleri', value: 'B2BOrder' },
      { label: 'Tüm Siparişler', value: 'AllOrder' },
    ],
    sortOptions: [
      { label: 'En Çok Farklı Bayi', value: 1 },
      { label: 'En Az Farklı Bayi', value: 2 },
      { label: 'En Yüksek Tutar', value: 3 },
      { label: 'En Düşük Tutar', value: 4 },
      { label: 'En Çok Sipariş', value: 5 },
      { label: 'En Az Sipariş', value: 6 },
    ],
    brandGroup: false,
  })

  const [state, setState] = useCustomReducer<IFavProductState>(getInitialState())

  const [items, setItems] = useState<ReturnModel[] | undefined>()
  const [daily, setDaily] = useState<DailyMetric[]>([])

  const initialStartDate = new Date()
  initialStartDate.setDate(1)

  const [startDate, setStartDate] = useState<Date>(initialStartDate)
  const [endDate, setEndDate] = useState<Date>(new Date())

  const [appliedTypeValue, setAppliedTypeValue] = useState<TypeValue | undefined>(undefined)

  const isAllOrderApplied = appliedTypeValue === 'AllOrder'
  const isB2BOrderApplied = appliedTypeValue === 'B2BOrder'
  const brandGroupApplied = state.brandGroup === true
  const mtGroupApplied = state.mtGroup === true

  const [orderOverlay, setOrderOverlay] = useState<OrderOverlayState>({ open: false })

  const detailSwal = useMemo(
    () =>
      Swal.mixin({
        width: 'auto',
        backdrop: true,
        confirmButtonText: 'KAPAT',
        confirmButtonColor: '#333',
      }),
    [],
  )

  const formatDateToInputValue = (date: any) =>
    date.toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(' ', 'T')

  const getDate = async (data: any, title: string) => {
    if (title === 'Start') setStartDate(data)
    else setEndDate(data)
  }

  const safeNumber = (v: any) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }

  const formatPriceType = (value: any) => (value == 'TL' || value == 'TRY' ? '₺' : value == 'USD' ? '$' : value == 'EUR' ? '€' : value)

  const openOrderOverlay = useCallback((title: string, dealerCode: string, orderNumbers: string[]) => {
    setOrderOverlay({ open: true, title, dealerCode, orderNumbers: orderNumbers ?? [] })
  }, [])

  const closeOrderOverlay = useCallback(() => setOrderOverlay({ open: false }), [])

  const validateAllOrderDates = useCallback(() => {
    if (!startDate || !endDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Başlangıç ve bitiş tarihi seçiniz!',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return false
    }

    const start = new Date(startDate)
    const end = new Date(endDate)

    if (end < start) {
      Swal.fire({
        icon: 'warning',
        title: 'Bitiş tarihi başlangıç tarihinden küçük olamaz!',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return false
    }

    const maxEnd = new Date(start)
    maxEnd.setMonth(maxEnd.getMonth() + 3)

    if (end > maxEnd) {
      Swal.fire({
        icon: 'warning',
        title: 'Tarih aralığı en fazla 3 ay olabilir!',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return false
    }

    return true
  }, [startDate, endDate])

  const getCategory = async () => {
    const data: ICategoryList[] = await getCategoryList()
    const categoryList: ISelectOption[] = []
    if (data) data.forEach(dt => categoryList.push({ value: dt.SapCode, label: `${dt.Name} (${dt.SapCode})` }))
    setState({ categoryList })
  }

  const GetQuota = async () => {
    const api = new GetQuotaListApi({})
    const data = await api.getQuotaList()
    const quotaList: ISelectOption[] = []
    if (data)
      data.forEach((dt: QuotaListReturnModel) => quotaList.push({ value: dt.ZZKTN, label: `${dt.ZZKTN} (${dt.ZZKTA})` }))
    setState({ KotaList: quotaList.length > 0 ? quotaList : undefined })
  }

  const GetMT = async () => {
    const api = new GetMTListApi({})
    const data = await api.getMTList()
    const mtList: ISelectOption[] = []
    if (data)
      data.forEach((dt: MTReturnModel) => mtList.push({ value: dt.PERSONEL_NUMBER, label: `${dt.PERSONEL_NAME} (${dt.PERSONEL_NUMBER})` }))
    setState({ MTList: mtList.length > 0 ? mtList : undefined })
  }

  const getBrand = async () => {
    const data: any[] = await getBrandApi()
    setState({ brandList: data })
  }

  useEffect(() => {
    getCompanies()
    getBrand()
    getCategory()
    GetQuota()
    GetMT()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCompanies = async () => {
    try {
      const companyList: ICompany[] = await getCompanyApi()
      const companiesOptions: ISelectOption[] = companyList.map(company => ({
        label: company.Name,
        value: company.Name,
      }))
      setState({ companyListOptions: companiesOptions })
    } catch (err) {
      console.log('Fetch Companies Error:', err)
    }
  }


  const getList = async () => {
    if (!state.typeOption) {
      return Swal.fire({
        icon: 'warning',
        title: 'İstatistik gösterilecek ürün tipi seçiniz!',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }

    const selectedType = state.typeOption.value as TypeValue
    const selectedIsAllOrder = selectedType === 'AllOrder'
    if (selectedIsAllOrder && !validateAllOrderDates()) return

    const req: RequestModel = {
      Type: selectedType,
      Company: state.companyListOption?.value?.toString(),
      ProductCode: state.product || undefined,
      DealerCode: state.dealerCode || undefined,
      GroupCode: state.GroupList ? state.GroupList.value.toString() : undefined,
      Kota: state.Kota ? state.Kota.value.toString() : undefined,
      MT: state.MT ? state.MT.value.toString() : undefined,
      Brand: state.brand ? state.brand.label.toString() : undefined,
      StartDate: getDateByTimezoneOffset(startDate) ?? undefined,
      EndDate: getDateByTimezoneOffset(endDate) ?? undefined,
      Sort: Number(state.Sort?.value) || undefined,
      BrandGroup: state.brandGroup ?? false,
      MTGroup: state.mtGroup ?? false,
    }

    props.showLoading()
    const api = new GetFavProductDataApi({})

    await api
      .getFavProductData(req)
      .then((result: any) => {
        const payload = result as FavProductStatsResponse
        setItems(payload?.Items ?? [])
        setDaily(payload?.Daily ?? [])
        setAppliedTypeValue(selectedType)
        props.hideLoading()
      })
      .catch((err: any) => {
        Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
        props.hideLoading()
      })
  }

  const buildDetailTitle = useCallback((p: any) => (brandGroupApplied ? `Marka: ${p.Brand ?? ''}` : `${p.ProductCode ?? ''}`), [brandGroupApplied])

  const handleDetail = useCallback(
    (dealerData: DealerDetails[], headerTitle: string) => {
      if (!dealerData || dealerData.length === 0) {
        detailSwal.fire({
          title: 'Hata',
          icon: 'error',
          html: `<div style="display:flex; justify-content:center; margin-bottom:1rem; font-size:16px;"><b>Veri bulunamadı.</b></div>`,
        })
        return
      }

      const showQty = isB2BOrderApplied || isAllOrderApplied
      const showUnit = isB2BOrderApplied || isAllOrderApplied
      const showUser = !isAllOrderApplied
      const showCount = isB2BOrderApplied || isAllOrderApplied

      const rows = (dealerData as any[])
        .map(x => {
          const dealerCodeClean = (x.DealerCode ?? '').replace('0000', '')
          const ordersEncoded = encodeURIComponent(JSON.stringify(x.OrderNumberList ?? []))

          return `
            <div style="display:flex; margin-bottom:0.5rem; border-bottom:1px solid #ccc; padding-bottom:0.5rem;">
              <div style="width:150px; text-align:left; align-content:center">${dealerCodeClean}</div>
              <div style="width:250px;">${x.DealerName ?? ''}</div>
              <div style="width:250px;">${x.PersonelName ?? ''}</div>

              ${showQty ? `<div style="width:180px; align-content:center">${x.Miktar ?? 0}</div>` : ``}
              ${showUnit ? `<div style="width:180px; align-content:center;">${safeNumber(x.BirimFiyat).toFixed(2)}</div>` : ``}
              ${showUnit ? `<div style="width:180px; align-content:center; text-align:center">${x.ParaBirimi ?? ''}</div>` : ``}
              ${showUnit ? `<div style="width:180px; align-content:center;">${safeNumber(x.Tutar).toFixed(2)}</div>` : ``}

              ${showUser ? `<div style="width:180px; align-content:center">${x.UserCountUnique ?? 0}</div>` : ``}

              ${showCount
              ? `<div class="js-order-detail"
                        data-title="${encodeURIComponent(headerTitle)}"
                        data-dealer="${encodeURIComponent(dealerCodeClean)}"
                        data-orders="${ordersEncoded}"
                        style="width:180px; align-content:center; cursor:pointer; text-decoration:underline;"
                      >${x.Count ?? 0}</div>`
              : ``
            }

              ${isB2BOrderApplied ? `<div style="width:180px; align-content:center">${x.CampaignLine ?? 0}</div>` : ``}
              ${isAllOrderApplied ? `<div style="width:180px; align-content:center">${x.B2B ?? 0}</div>` : ``}
              ${isAllOrderApplied ? `<div style="width:180px; align-content:center">${x.SAP ?? 0}</div>` : ``}
            </div>
          `
        })
        .join('')

      const headers = `
        <div style="display:flex; margin-bottom:1rem; font-size:16px;">
          <b style="width:150px; text-align:left;">Bayi Kodu</b>
          <b style="width:250px;">Bayi Adı</b>
          <b style="width:250px;">Müşteri Temsilcisi</b>

          ${showQty ? `<b style="width:180px;">Ürün Adet</b>` : ``}
          ${showUnit ? `<b style="width:180px;">Birim Fiyat</b>` : ``}
          ${showUnit ? `<b style="width:180px;">Para Birimi</b>` : ``}
          ${showUnit ? `<b style="width:180px;">Tutar</b>` : ``}
          ${showUser ? `<b style="width:180px;">Kullanıcı Sayısı</b>` : ``}
          ${showCount ? `<b style="width:180px;">${isB2BOrderApplied ? 'Sipariş Satırı Sayısı' : 'Toplam Sipariş Sayısı'}</b>` : ``}
           ${isB2BOrderApplied ? `<b style="width:180px;">Kampanya Sayısı</b>` : ``}

          ${isAllOrderApplied ? `<b style="width:180px;">B2B Siparişleri</b>` : ``}
          ${isAllOrderApplied ? `<b style="width:180px;">SAP Siparişleri</b>` : ``}
        </div>
      `

      detailSwal.fire({
        title: 'Detay',
        icon: 'info',
        html: `<div>
          <b style="display:block; margin-bottom:1.5rem; font-size:18px;">Bayi Bazlı Ürün İstatistikleri - ${headerTitle}</b>
          ${headers}
          ${rows}
        </div>`,
        didOpen: () => {
          const popup = Swal.getPopup()
          if (!popup) return
          const nodes = popup.querySelectorAll<HTMLElement>('.js-order-detail')
          nodes.forEach(n => {
            n.onclick = () => {
              const title = decodeURIComponent(n.dataset.title ?? '')
              const d = decodeURIComponent(n.dataset.dealer ?? '')
              const orders = JSON.parse(decodeURIComponent(n.dataset.orders ?? '[]')) as string[]
              openOrderOverlay(title, d, orders)
            }
          })
        },
      })
    },
    [brandGroupApplied, detailSwal, isAllOrderApplied, isB2BOrderApplied, openOrderOverlay],
  )

  const generateColumn = (Header: any, accessor: string) => ({ Header, accessor })

  const tableColumns = useMemo(() => {
    const cols: any[] = [{ Header: '#', accessor: 'order' }]

    if (!brandGroupApplied) {
      cols.push({ Header: 'Ürün Kodu', accessor: 'ProductCode' })
      cols.push({ Header: 'Malzeme Numarası', accessor: 'MATNR' })
      cols.push({ Header: 'Grup Kodu', accessor: 'GroupCode' })
      cols.push({ Header: 'Marka', accessor: 'Brand' })
      cols.push({ Header: 'Kota', accessor: 'Kota' })
      cols.push({ Header: 'Şirket', accessor: 'Company' })
      cols.push({ Header: 'Ürün Müdürü', accessor: 'MudName' })
      if (mtGroupApplied) {
        cols.push({ Header: 'Personel Adı', accessor: 'PersonelName' })
      }
      cols.push({ Header: 'Detay', accessor: 'DetailsModel' })
      cols.push({ Header: 'Bayi Sayısı', accessor: 'RepeatCountUnique' })
    } else {
      cols.push({ Header: 'Marka', accessor: 'Brand' })
      cols.push({ Header: 'Şirket', accessor: 'Company' })
      if (mtGroupApplied) {
        cols.push({ Header: 'Personel Adı', accessor: 'PersonelName' })
      }
      cols.push({ Header: 'Detay', accessor: 'DetailsModel' })
      cols.push({ Header: 'Bayi Sayısı', accessor: 'RepeatCountUnique' })
    }

    if (!isAllOrderApplied) cols.push({ Header: 'Kullanıcı Sayısı', accessor: 'UserCountUnique' })

    if (isB2BOrderApplied) {
      cols.push({ Header: 'Toplam Adet', accessor: 'TotalQuantity' })
      cols.push({ Header: 'Sipariş Satırı Sayısı', accessor: 'RepeatCount' })
      cols.push({ Header: 'Kampanya Sayısı', accessor: 'CampaignLine' })
      cols.push(generateColumn(<span style={{ float: 'right' }}>Birim Fiyatı </span>, 'unitPrice'))
      cols.push(generateColumn(<span style={{ textAlign: 'center', display: 'block' }}>Para Birimi </span>, 'priceType'))
      cols.push(generateColumn(<span style={{ float: 'right' }}>Toplam Tutar </span>, 'totalPrice'))
    }

    if (isAllOrderApplied) {
      cols.push({ Header: 'Sipariş Sayısı', accessor: 'RepeatCount' })
      cols.push(generateColumn(<span style={{ float: 'right' }}>Birim Fiyatı </span>, 'unitPrice'))
      cols.push(generateColumn(<span style={{ textAlign: 'center', display: 'block' }}>Para Birimi </span>, 'priceType'))
      cols.push(generateColumn(<span style={{ float: 'right' }}>Toplam Tutar </span>, 'totalPrice'))
    }

    return cols
  }, [brandGroupApplied, mtGroupApplied, isAllOrderApplied, isB2BOrderApplied])

  const tableData = useMemo(() => {
    if (!items || items.length === 0) return []

    return (items as any[]).map((p: any, index) => {
      const title = buildDetailTitle(p)

      return {
        order: index + 1,
        ProductCode:
          !brandGroupApplied && p.ProductID !== 0 ? (
            <Link target="_blank" to={`/product/detail/${p.ProductID}?from=productList`}>
              {p.ProductCode}
            </Link>
          ) : (
            p.ProductCode
          ),
        MATNR: p.MATNR,
        Company: p.Company,
        Brand: p.Brand,
        Kota: p.Quota,
        GroupCode: !brandGroupApplied ? state.categoryList?.find(q => q.value === p.GroupCode)?.label || p.GroupCode : undefined,
        MudName: p.MudName,
        PersonelName: p.PersonelName ?? '',

        RepeatCountUnique: p.RepeatCountUnique,
        UserCountUnique: p.UserCountUnique ?? 0,

        totalPrice:
          p.ParaBirimi == 'MIXED' ? (
            <div style={{ textAlign: 'right' }}>MIXED</div>
          ) : (
            <MoneySmallDecimals value={p.Tutar ?? '0,00'} currency={formatPriceType(p.ParaBirimi)} />
          ),
        unitPrice:
          p.ParaBirimi == 'MIXED' ? (
            <div style={{ textAlign: 'right' }}>MIXED</div>
          ) : (
            <MoneySmallDecimals value={p.BirimFiyat ?? '0,00'} currency={formatPriceType(p.ParaBirimi)} />
          ),
        priceType: <div style={{ textAlign: 'center' }}>{p.ParaBirimi}</div>,

        TotalQuantity: p.TotalQuantity ?? 0,
        RepeatCount: p.RepeatCount ?? 0,
        CampaignLine: p.CampaignLine ?? 0,

        DetailsModel: (
          <IPButton
            text="Detay"
            onClick={() => handleDetail(p.DealerList, title)}
            type="button"
            style={{ padding: '0px', width: '120px', height: '30px', color: 'white', borderColor: 'red' }}
            className="btn btn-danger"
          />
        ),
      }
    })
  }, [items, buildDetailTitle, brandGroupApplied, mtGroupApplied, handleDetail, state.categoryList])

  // Daily: currency group + date sort
  const dailyByCurrency = useMemo(() => {
    const map = new Map<string, DailyMetric[]>()

      ; (daily ?? []).forEach(d => {
        const cur = d.Currency ?? 'NO_CURRENCY'
        const arr = map.get(cur) ?? []
        arr.push(d)
        map.set(cur, arr)
      })

    return Array.from(map.entries()).map(([k, arr]) => [k, arr.slice().sort((a, b) => a.Date.localeCompare(b.Date))] as [
      string,
      DailyMetric[],
    ])
  }, [daily])

  // Daily (currency bağımsız): aynı Date için tüm currency satırlarını topla
  const dailyAllCurrency = useMemo(() => {
    const map = new Map<string, DailyMetric>()

      ; (daily ?? []).forEach(d => {
        const key = d.Date
        const cur = map.get(key)

        if (!cur) {
          map.set(key, {
            Date: d.Date,
            Currency: 'ALL',

            EventCount: d.EventCount ?? 0,
            UniqueDealerCount: d.UniqueDealerCount ?? 0,
            UniqueUserCount: d.UniqueUserCount ?? 0,
            UniqueProductCount: d.UniqueProductCount ?? 0,

            OrderLineCount: d.OrderLineCount ?? 0,
            OrderCount: d.OrderCount ?? 0,
            Quantity: d.Quantity ?? 0,
            Amount: d.Amount ?? 0,
            UnitPrice: d.UnitPrice ?? 0,

            CampaignLine: d.CampaignLine ?? 0,
            B2BOrderCount: d.B2BOrderCount ?? 0,
            SAPOrderCount: d.SAPOrderCount ?? 0,
          })
          return
        }

        // aynı gün: toplamsal metrikleri topla
        cur.EventCount += d.EventCount ?? 0
        cur.UniqueDealerCount += d.UniqueDealerCount ?? 0
        cur.UniqueUserCount += d.UniqueUserCount ?? 0
        cur.UniqueProductCount += d.UniqueProductCount ?? 0

        cur.OrderLineCount += d.OrderLineCount ?? 0
        cur.OrderCount += d.OrderCount ?? 0
        cur.Quantity += d.Quantity ?? 0
        cur.Amount += d.Amount ?? 0

        cur.CampaignLine += d.CampaignLine ?? 0
        cur.B2BOrderCount += d.B2BOrderCount ?? 0
        cur.SAPOrderCount += d.SAPOrderCount ?? 0

        // UnitPrice: günlük agregasyonda anlamlı bir “ortalama” istersen weighted average yapalım.
        // (Toplam Tutar / Toplam Adet) yaklaşımı en doğru olur.
        // Burada UnitPrice'ı sonradan hesaplayacağız.
        cur.UnitPrice = 0
      })

    const arr = Array.from(map.values()).sort((a, b) => a.Date.localeCompare(b.Date))

    // Weighted average unit price (Amount / Quantity), quantity 0 ise 0
    arr.forEach(x => {
      const q = Number(x.Quantity ?? 0)
      const a = Number(x.Amount ?? 0)
      x.UnitPrice = q > 0 ? a / q : 0
    })

    return arr
  }, [daily])


  const setNumberFormatByHeader = (ws: XLSX.WorkSheet, headers: string[], format: string) => {
    if (!ws['!ref']) return
    const range = XLSX.utils.decode_range(ws['!ref'])
    const headerRow = range.s.r

    const headerMap = new Map<string, number>()
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r: headerRow, c })
      const cell = ws[addr]
      const text = cell?.v?.toString()?.trim()
      if (text) headerMap.set(text, c)
    }

    headers.forEach(h => {
      const col = headerMap.get(h)
      if (col === undefined) return
      for (let r = headerRow + 1; r <= range.e.r; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: col })
        const cell = ws[addr]
        if (!cell) continue
        if (cell.t === 'n') (cell as any).z = format
      }
    })
  }

  const excelButton = async () => {
    try {
      if (!items || items.length === 0) {
        return Swal.fire({ icon: 'error', title: 'Excele atılacak veri bulunmamaktadır.', allowOutsideClick: true, backdrop: true })
      }

      const rows = (items as any[]).map((p, index) => {
        const base: any = {
          Sıra: index + 1,
          Marka: p.Brand,
          Şirket: p.Company,

        }

        if (!isAllOrderApplied) base['Kullanıcı Sayısı'] = p.UserCountUnique ?? 0

        if (!brandGroupApplied) {
          base['Ürün Kodu'] = p.ProductCode
          base['Malzeme Numarası'] = p.MATNR
          base['Grup Kodu'] = state.categoryList?.find(q => q.value === p.GroupCode)?.label || p.GroupCode
          base['Kota'] = p.Quota
          base['Ürün Müdürü'] = p.MudName

          if (mtGroupApplied) {
            base['Personel Adı'] = p.PersonelName ?? ''
          }
        }

        base['Bayi Sayısı'] = p.RepeatCountUnique

        if (isB2BOrderApplied) {
          base['Toplam Adet'] = p.TotalQuantity ?? 0
          base['Sipariş Satırı Sayısı'] = p.RepeatCount ?? 0
          base['Kampanya Sayısı'] = p.CampaignLine ?? 0
          base['Birim Fiyat'] = safeNumber(p.BirimFiyat)
          base['Para Birimi'] = p.ParaBirimi ?? ''
          base['Toplam Tutar'] = safeNumber(p.Tutar)
        }

        if (isAllOrderApplied) {
          base['Sipariş Sayısı'] = p.RepeatCount ?? 0
          base['Birim Fiyat'] = safeNumber(p.BirimFiyat)
          base['Para Birimi'] = p.ParaBirimi ?? ''
          base['Toplam Tutar'] = safeNumber(p.Tutar)
        }

        return base
      })

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(rows)
      XLSX.utils.book_append_sheet(wb, ws, 'Özet')
      setNumberFormatByHeader(ws, ['Birim Fiyat', 'Toplam Tutar'], '#,##0.00')

      if (daily && daily.length > 0) {
        const dailyRows = daily.map(d => ({
          Tarih: d.Date,
          'Para Birimi': d.Currency ?? '',
          'Olay Sayısı': d.EventCount,
          'Tekil Bayi': d.UniqueDealerCount,
          'Tekil Kullanıcı': d.UniqueUserCount,
          'Tekil Ürün': d.UniqueProductCount,
          'Sipariş Satırı': d.OrderLineCount,
          'Sipariş Sayısı': d.OrderCount,
          'Toplam Adet': d.Quantity,
          'Kampanya Satırı': d.CampaignLine,
          'Toplam Tutar': d.Amount,
          'Birim Fiyat': d.UnitPrice,
          'B2B Sipariş': d.B2BOrderCount,
          'SAP Sipariş': d.SAPOrderCount,
        }))
        const wsDaily = XLSX.utils.json_to_sheet(dailyRows)
        XLSX.utils.book_append_sheet(wb, wsDaily, 'Günlük')
        setNumberFormatByHeader(wsDaily, ['Toplam Tutar', 'Birim Fiyat'], '#,##0.00')
      }

      XLSX.writeFile(wb, `Genel_${brandGroupApplied ? 'Marka' : 'Ürün'}_İstatistikleri_${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.description || 'Excel oluşturulurken hata oluştu.', allowOutsideClick: true, backdrop: true })
    }
  }

  const excelButtonWithDetail = async () => {
    try {
      // 1) Veri kontrolü
      if (!items || items.length === 0) {
        return Swal.fire({
          icon: 'error',
          title: 'Excele atılacak veri bulunmamaktadır.',
          allowOutsideClick: true,
          backdrop: true,
        })
      }

      const type = appliedTypeValue
      if (!type) {
        return Swal.fire({
          icon: 'warning',
          title: 'Önce filtreleyip listeyi getiriniz.',
          allowOutsideClick: false,
          backdrop: true,
        })
      }

      // 2) Tip flag'leri (sayfanın genel kurgusuyla aynı)
      const isFav = type === 'Fav'
      const isRecent = type === 'Recent'
      const isFavOrRecent = isFav || isRecent
      const isB2B = type === 'B2BOrder'
      const isAll = type === 'AllOrder'
      const includeOrders = isB2B || isAll

      // 3) Header/kolon sırasını stabil tutmak için helper
      const pushIf = (obj: any, cond: boolean, key: string, value: any) => {
        if (cond) obj[key] = value
      }

      // 4) ÖZET satırları
      const summaryRows = (items as any[]).map((p, index) => {
        const row: any = { Sıra: index + 1 }

        // BrandGroup açıkken ürün bazlı alanlar olmayabilir; o yüzden koşulluyoruz
        pushIf(row, !brandGroupApplied, 'Ürün Kodu', p.ProductCode ?? '')
        pushIf(row, !brandGroupApplied, 'Malzeme Numarası', p.MATNR ?? '')
        pushIf(
          row,
          !brandGroupApplied,
          'Grup Kodu',
          state.categoryList?.find(q => q.value === p.GroupCode)?.label || p.GroupCode || '',
        )
        pushIf(row, !brandGroupApplied, 'Kota', p.Quota ?? '')
        pushIf(row, !brandGroupApplied, 'Ürün Müdürü', p.MudName ?? '')

        row['Marka'] = p.Brand ?? ''
        row['Şirket'] = p.Company ?? ''

        if (mtGroupApplied) {
          row['Personel Adı'] = p.PersonelName ?? ''
        }

        row['Bayi Sayısı'] = p.RepeatCountUnique ?? 0

        // AllOrder'da kullanıcı sayısı yok
        pushIf(row, !isAll, 'Kullanıcı Sayısı', p.UserCountUnique ?? 0)

        if (isB2B) {
          row['Toplam Adet'] = p.TotalQuantity ?? 0
          row['Sipariş Satırı Sayısı'] = p.RepeatCount ?? 0
          row['Kampanya Sayısı'] = p.CampaignLine ?? 0
          row['Birim Fiyat'] = safeNumber(p.BirimFiyat)
          row['Para Birimi'] = p.ParaBirimi ?? ''
          row['Toplam Tutar'] = safeNumber(p.Tutar)
        }

        if (isAll) {
          row['Sipariş Sayısı'] = p.RepeatCount ?? 0
          row['Birim Fiyat'] = safeNumber(p.BirimFiyat)
          row['Para Birimi'] = p.ParaBirimi ?? ''
          row['Toplam Tutar'] = safeNumber(p.Tutar)
        }

        return row
      })

      // 5) DETAY ve (opsiyonel) SİPARİŞ SATIRLARI
      const detailRows: any[] = []
      const orderLineRows: any[] = []

      // Detay ve Sipariş Satırları: Brand/MT Group seçiliyken de oluştur
      if (true) {
        for (let pIndex = 0; pIndex < (items as any[]).length; pIndex++) {
          const item: any = (items as any[])[pIndex]
          const dealers: any[] = item?.DealerList || []
          // MT Group veya Brand Group seçiliyken dealers boş ise, item-level row ekle
          if (dealers.length === 0 && (mtGroupApplied || brandGroupApplied)) {
            const row: any = {
              'Satır Tipi': 'DETAY',
              'Ürün Sıra': pIndex + 1,
              'Bayi Detay Sıra': 0,
              'Bayi Kodu': '',
              'Bayi Adı': '',
              'Müşteri Temsilcisi': '',
            }

            row['Marka'] = item?.Brand ?? ''
            row['Şirket'] = item?.Company ?? ''

            pushIf(row, !brandGroupApplied, 'Ürün Kodu', item?.ProductCode ?? '')
            pushIf(row, !brandGroupApplied, 'Malzeme Numarası', item?.MATNR ?? '')
            pushIf(
              row,
              !brandGroupApplied,
              'Grup Kodu',
              state.categoryList?.find(q => q.value === item?.GroupCode)?.label || item?.GroupCode || '',
            )
            pushIf(row, !brandGroupApplied, 'Kota', item?.Quota ?? '')
            pushIf(row, !brandGroupApplied, 'Ürün Müdürü', item?.MudName ?? '')

            if (isFavOrRecent) {
              row['Kullanıcı Sayısı'] = item?.UserCountUnique ?? 0
              pushIf(row, typeof item?.RepeatCount !== 'undefined', 'Toplam', item?.RepeatCount ?? 0)
            }

            if (isB2B) {
              row['Ürün Adet'] = item?.TotalQuantity ?? 0
              row['Sipariş Satırı Sayısı'] = item?.RepeatCount ?? 0
              row['Kampanya Sayısı'] = item?.CampaignLine ?? 0
              row['Birim Fiyat'] = safeNumber(item?.BirimFiyat)
              row['Para Birimi'] = item?.ParaBirimi ?? ''
              row['Tutar'] = safeNumber(item?.Tutar)
              row['Sipariş Adedi'] = 0
            }

            if (isAll) {
              row['Ürün Adet'] = item?.TotalQuantity ?? 0
              row['Birim Fiyat'] = safeNumber(item?.BirimFiyat)
              row['Para Birimi'] = item?.ParaBirimi ?? ''
              row['Tutar'] = safeNumber(item?.Tutar)
              row['Toplam Sipariş Sayısı'] = item?.RepeatCount ?? 0
              row['B2B Siparişleri'] = 0
              row['SAP Siparişleri'] = 0
              row['Sipariş Adedi'] = 0
            }

            detailRows.push(row)
            continue
          }

          for (let dIndex = 0; dIndex < dealers.length; dIndex++) {
            const r: any = dealers[dIndex]
            const dealerCodeClean = (r?.DealerCode ?? '').replace('0000', '')

            const row: any = {
              'Satır Tipi': 'DETAY',
              'Ürün Sıra': pIndex + 1,
              'Bayi Detay Sıra': dIndex + 1,
              'Bayi Kodu': dealerCodeClean,
              'Bayi Adı': r?.DealerName ?? '',
              'Müşteri Temsilcisi': r?.PersonelName ?? '',
            }

            // Ürün bilgileri (brandGroup kapalıysa)
            pushIf(row, !brandGroupApplied, 'Ürün Kodu', item?.ProductCode ?? '')
            pushIf(row, !brandGroupApplied, 'Malzeme Numarası', item?.MATNR ?? '')
            pushIf(
              row,
              !brandGroupApplied,
              'Grup Kodu',
              state.categoryList?.find(q => q.value === item?.GroupCode)?.label || item?.GroupCode || '',
            )
            pushIf(row, !brandGroupApplied, 'Kota', item?.Quota ?? '')
            pushIf(row, !brandGroupApplied, 'Ürün Müdürü', item?.MudName ?? '')

            // Brand/Company her durumda
            row['Marka'] = item?.Brand ?? ''
            row['Şirket'] = item?.Company ?? ''

            // Fav/Recent: user count var, order sayısı metrik olabilir (r.Count)
            if (isFavOrRecent) {
              row['Kullanıcı Sayısı'] = r?.UserCountUnique ?? 0
              // Eğer backend gönderiyorsa:
              pushIf(row, typeof r?.Count !== 'undefined', 'Toplam', r?.Count ?? 0)
            }

            if (isB2B) {
              row['Ürün Adet'] = r?.Miktar ?? 0
              row['Sipariş Satırı Sayısı'] = r?.Count ?? 0
              row['Kampanya Sayısı'] = r?.CampaignLine ?? 0

              row['Birim Fiyat'] = safeNumber(r?.BirimFiyat)
              row['Para Birimi'] = r?.ParaBirimi ?? ''
              row['Tutar'] = safeNumber(r?.Tutar)

              const orderNumbers: string[] = (r?.OrderNumberList ?? []) as string[]
              row['Sipariş Adedi'] = orderNumbers.length

              if (orderNumbers.length > 0) {
                orderNumbers.forEach((ordNo: string, ordIndex: number) => {
                  const ol: any = {
                    'Satır Tipi': 'SİPARİŞ_SATIRI',
                    'Ürün Sıra': pIndex + 1,
                    'Sipariş Sıra': ordIndex + 1,
                    'Sipariş Numarası': ordNo,
                    'Bayi Kodu': dealerCodeClean,
                    'Bayi Adı': r?.DealerName ?? '',
                    'Müşteri Temsilcisi': r?.PersonelName ?? '',
                    'Para Birimi': r?.ParaBirimi ?? '',
                    Marka: item?.Brand ?? '',
                    Şirket: item?.Company ?? '',
                  }

                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Ürün Kodu', item?.ProductCode ?? '')
                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Malzeme Numarası', item?.MATNR ?? '')
                  pushIf(
                    ol,
                    !brandGroupApplied && !mtGroupApplied,
                    'Grup Kodu',
                    state.categoryList?.find(q => q.value === item?.GroupCode)?.label || item?.GroupCode || '',
                  )
                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Kota', item?.Quota ?? '')
                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Ürün Müdürü', item?.MudName ?? '')

                  orderLineRows.push(ol)
                })
              }
            }

            if (isAll) {
              row['Ürün Adet'] = r?.Miktar ?? 0
              row['Birim Fiyat'] = safeNumber(r?.BirimFiyat)
              row['Para Birimi'] = r?.ParaBirimi ?? ''
              row['Tutar'] = safeNumber(r?.Tutar)

              row['Toplam Sipariş Sayısı'] = r?.Count ?? 0
              row['B2B Siparişleri'] = r?.B2B ?? 0
              row['SAP Siparişleri'] = r?.SAP ?? 0

              const orderNumbers: string[] = (r?.OrderNumberList ?? []) as string[]
              row['Sipariş Adedi'] = orderNumbers.length

              if (orderNumbers.length > 0) {
                orderNumbers.forEach((ordNo: string, ordIndex: number) => {
                  const ol: any = {
                    'Satır Tipi': 'SİPARİŞ_SATIRI',
                    'Ürün Sıra': pIndex + 1,
                    'Sipariş Sıra': ordIndex + 1,
                    'Sipariş Numarası': ordNo,
                    'Bayi Kodu': dealerCodeClean,
                    'Bayi Adı': r?.DealerName ?? '',
                    'Müşteri Temsilcisi': r?.PersonelName ?? '',
                    'Para Birimi': r?.ParaBirimi ?? '',
                    Marka: item?.Brand ?? '',
                    Şirket: item?.Company ?? '',
                  }

                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Ürün Kodu', item?.ProductCode ?? '')
                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Malzeme Numarası', item?.MATNR ?? '')
                  pushIf(
                    ol,
                    !brandGroupApplied && !mtGroupApplied,
                    'Grup Kodu',
                    state.categoryList?.find(q => q.value === item?.GroupCode)?.label || item?.GroupCode || '',
                  )
                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Kota', item?.Quota ?? '')
                  pushIf(ol, !brandGroupApplied && !mtGroupApplied, 'Ürün Müdürü', item?.MudName ?? '')

                  orderLineRows.push(ol)
                })
              }
            }

            detailRows.push(row)
          }
        }
      }

      // 6) Workbook + sheet'ler
      const wb = XLSX.utils.book_new()

      const wsSummary = XLSX.utils.json_to_sheet(summaryRows)
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Özet')

      if (detailRows.length > 0) {
        const wsDetail = XLSX.utils.json_to_sheet(detailRows)
        XLSX.utils.book_append_sheet(wb, wsDetail, 'Detay')
      }

      if (includeOrders && orderLineRows.length > 0) {
        const wsOrders = XLSX.utils.json_to_sheet(orderLineRows)
        XLSX.utils.book_append_sheet(wb, wsOrders, 'Sipariş Satırları')
      }

      // İstersen Günlük sheet'i de ekleyelim (excelButton ile aynı mantık)
      if (daily && daily.length > 0) {
        const dailyRows = daily.map(d => ({
          Tarih: d.Date,
          'Para Birimi': d.Currency ?? '',
          'Olay Sayısı': d.EventCount,
          'Tekil Bayi': d.UniqueDealerCount,
          'Tekil Kullanıcı': d.UniqueUserCount,
          'Tekil Ürün': d.UniqueProductCount,
          'Sipariş Satırı': d.OrderLineCount,
          'Sipariş Sayısı': d.OrderCount,
          'Toplam Adet': d.Quantity,
          'Kampanya Satırı': d.CampaignLine,
          'Toplam Tutar': d.Amount,
          'Birim Fiyat': d.UnitPrice,
          'B2B Sipariş': d.B2BOrderCount,
          'SAP Sipariş': d.SAPOrderCount,
        }))
        const wsDaily = XLSX.utils.json_to_sheet(dailyRows)
        XLSX.utils.book_append_sheet(wb, wsDaily, 'Günlük')
        setNumberFormatByHeader(wsDaily, ['Toplam Tutar', 'Birim Fiyat'], '#,##0.00')
      }

      // 7) Number format
      setNumberFormatByHeader(wsSummary, ['Birim Fiyat', 'Toplam Tutar'], '#,##0.00')
      if (detailRows.length > 0) {
        const wsDetail = wb.Sheets['Detay']
        if (wsDetail) setNumberFormatByHeader(wsDetail, ['Birim Fiyat', 'Tutar', 'Toplam Tutar'], '#,##0.00')
      }

      // 8) Otomatik kolon genişliği (stabil ve bakımı kolay)
      const autoFitCols = (ws: XLSX.WorkSheet, sampleRows: any[], maxWch = 60) => {
        if (!sampleRows || sampleRows.length === 0) return
        const headers = Object.keys(sampleRows[0] ?? {})
        const widths = headers.map(h => {
          let maxLen = (h?.toString()?.length ?? 10) + 2
          for (let i = 0; i < Math.min(sampleRows.length, 300); i++) {
            const v = sampleRows[i]?.[h]
            const s = v == null ? '' : String(v)
            if (s.length + 2 > maxLen) maxLen = s.length + 2
          }
          return Math.min(maxWch, Math.max(10, maxLen))
        })
          ; (ws as any)['!cols'] = widths.map(w => ({ wch: w }))
      }

      autoFitCols(wsSummary, summaryRows)
      if (detailRows.length > 0) {
        const wsDetail = wb.Sheets['Detay']
        if (wsDetail) autoFitCols(wsDetail, detailRows)
      }
      if (includeOrders && orderLineRows.length > 0) {
        const wsOrders = wb.Sheets['Sipariş Satırları']
        if (wsOrders) autoFitCols(wsOrders, orderLineRows)
      }

      // 9) Dosya adı
      const typeLabel =
        type === 'Fav'
          ? 'Favoriler'
          : type === 'Recent'
            ? 'İnceleme'
            : type === 'B2BOrder'
              ? 'B2B_Siparişleri'
              : 'Tüm_Siparişler'

      const scopeLabel = (mtGroupApplied && brandGroupApplied) ? 'MT-Marka' : brandGroupApplied ? 'Marka' : mtGroupApplied ? 'MT' : 'Ürün'
      const fileName = `Genel_${scopeLabel}_Detay_İstatistikleri_${typeLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`

      try {
        XLSX.writeFile(wb, fileName)
      } catch (writeErr: any) {
        console.error('XLSX.writeFile error:', writeErr)
        throw writeErr
      }
    } catch (err: any) {
      console.error('Excel generation error:', err)
      Swal.fire({
        icon: 'error',
        title: err?.message || String(err) || 'Excel oluşturulurken hata oluştu.',
        allowOutsideClick: true,
        backdrop: true,
      })
    }
  }


  return (
    <>
      <OrderOverlay state={orderOverlay} onClose={closeOrderOverlay} />

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="h4">Genel {brandGroupApplied ? 'Marka' : 'Ürün'} İstatistikleri</span>

          <div className="d-flex align-items-center">
            <span>Excel: </span>
            <button
              type="button"
              style={{
                border: '1px solid #2e7d32',
                borderRadius: 0,
                width: '29px',
                height: '29px',
                backgroundColor: 'white',
                margin: '0 10px',
              }}
              onClick={excelButton}
              className="btn text-capitalize background-white-important d-flex align-items-center"
            >
              <i className="fas fa-file-excel" style={{ marginLeft: '-6px', fontSize: '18px', color: '#2e7d32' }} />
            </button>

            <span>Detaylı Excel: </span>
            <button
              type="button"
              style={{
                border: '1px solid #2e7d32',
                borderRadius: 0,
                width: '29px',
                height: '29px',
                backgroundColor: 'white',
                margin: '0 10px',
              }}
              onClick={excelButtonWithDetail}
              className="btn text-capitalize background-white-important d-flex align-items-center"
            >
              <i className="fas fa-file-excel" style={{ marginLeft: '-6px', fontSize: '18px', color: '#2e7d32' }} />
            </button>
          </div>
        </div>

        <div className="card-body">
          <form className="formbanner">
            <div className="row">
              <div className="form-group col-md-2">
                <InputCampaign
                  type="date"
                  id="StartDate"
                  name="StartDate"
                  label="Başlangıç Tarihi"
                  value={formatDateToInputValue(startDate)}
                  onChange={e => getDate((e as any).target.value, 'Start')}
                  isActive
                />
              </div>

              <div className="form-group col-md-2">
                <InputCampaign
                  type="date"
                  id="EndDate"
                  name="EndDate"
                  label="Bitiş Tarihi"
                  value={formatDateToInputValue(endDate)}
                  onChange={e => getDate((e as any).target.value, 'End')}
                  isActive
                />
              </div>

              <div className="form-group col-md-2">
                <IPSelectBox
                  isClearable
                  id="Şirket"
                  name="Şirket"
                  placeholder="Şirket seçiniz..."
                  options={state.companyListOptions}
                  value={state.companyListOption}
                  onChangeSingle={option => setState({ companyListOption: option })}
                />
              </div>

              <div className="form-group col-md-2">
                <IPSelectBox
                  isClearable
                  id="Type"
                  name="Type"
                  placeholder="Tip seçiniz..."
                  options={state.typeOptions}
                  value={state.typeOption}
                  onChangeSingle={option => setState({ typeOption: option })}
                />
              </div>

              <div className="form-group col-md-2">
                <IPInput
                  type="text"
                  id="DealerCode"
                  className="form-control"
                  name="DealerCode"
                  placeholder="Bayi Kodu"
                  onChange={e => setState({ dealerCode: (e as any).target.value })}
                  value={state.dealerCode}
                />
              </div>
              <div className="form-group col-md-1">
                <IPCheckbox
                  id="BrandGroup"
                  name="BrandGroup"
                  label="Marka Bazlı Grupla"
                  onCheckedChange={() => setState({ brandGroup: !state.brandGroup })}
                  checked={state.brandGroup}
                />
              </div>
              <div className="form-group col-md-1">
                <IPCheckbox
                  id="MTGroup"
                  name="MTGroup"
                  label="MT Bazlı Grupla"
                  onCheckedChange={() => setState({ mtGroup: !state.mtGroup })}
                  checked={state.mtGroup}
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-2">
                <IPSelectBox
                  isClearable
                  id="Brand"
                  name="Brand"
                  placeholder="Marka seçiniz..."
                  options={state.brandList?.map(brand => ({ label: brand.Name, value: brand.Id })) || []}
                  value={state.brand as unknown as ISelectOption}
                  onChangeSingle={option => setState({ brand: option })}
                />
              </div>

              <div className="form-group col-md-2">
                <IPInput
                  type="text"
                  id="Title"
                  className="form-control"
                  name="Title"
                  placeholder="Ürün Kodu"
                  onChange={e => setState({ product: (e as any).target.value })}
                  value={state.product}
                  disabled={brandGroupApplied}
                />
              </div>

              <div className="form-group col-md-2">
                <IPSelectBox
                  placeholder="Grup Kodu"
                  value={state.GroupList}
                  options={state.categoryList}
                  isClearable
                  onChangeSingle={options => setState({ GroupList: options })}
                  isDisabled={brandGroupApplied}
                />
              </div>

              <div className="form-group col-md-2">
                <IPSelectBox
                  isClearable
                  id="Kota"
                  name="Kota"
                  placeholder="Kota seçiniz..."
                  options={state.KotaList}
                  value={state.Kota}
                  onChangeSingle={option => setState({ Kota: option })}
                  isDisabled={brandGroupApplied}
                />
              </div>

              <div className="form-group col-md-2">
                <IPSelectBox
                  isClearable
                  id="MT"
                  name="MT"
                  placeholder="MT seçiniz..."
                  options={state.MTList}
                  value={state.MT}
                  onChangeSingle={option => setState({ MT: option })}
                  isDisabled={brandGroupApplied}
                />
              </div>

              <div className="form-group col-md-2">
                <IPSelectBox
                  isClearable
                  id="Sort"
                  name="Sort"
                  placeholder="Sıralama..."
                  options={state.sortOptions}
                  value={state.Sort}
                  onChangeSingle={option => setState({ Sort: option })}
                />
              </div>

              <div className="col-md-1 mb-3">
                <IPButton
                  type="button"
                  className="btn btn-success"
                  style={{ width: '25%' }}
                  text={t('pages.admin.dealerAndOrderReport.handcraftedOrdersList.handcraftedOrdersList.filtre')}
                  onClick={getList}
                />
              </div>
            </div>
          </form>

          {/* TABLO */}
          <div className="m-4">
            <label style={{ color: 'red' }}>Fazla veri gelmesi durumunda MT bilgisi verilememektedir.</label>
            <br />
            <label style={{ color: 'red' }}>Sıralama özelliği yalnızca siparişlerde kullanılmaktadır.</label>
            <br />
            <label style={{ color: 'red' }}>Marka gruplamasını seçtikten sonra tekrardan filtrelemeniz gerekmektedir.</label>
            <br />
            <label style={{ color: 'red' }}>MT bilgisi siparişlerdeki "Muhatap" alanındaki "Satış Temsilcisi" bilgisiyle eşleşir.</label>
            <br />
            <label style={{ color: 'red' }}>MT filtresi yalnızca "Tüm Siparişler" seçeneği ile birlikte çalışmaktadır.</label>
            <br />
            <Table columns={tableColumns} data={tableData} currentPage={0} headerName="İstatistikler" columnCount={6} />
          </div>

          {/* GÜNLÜK GRAFİKLER (TABLO ALTINDA) */}
          <div className="m-4">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Günlük Grafikler</div>

            {dailyAllCurrency.length === 0 ? (
              <div style={{ padding: 8, border: '1px dashed #ccc', borderRadius: 8 }}>Grafik için veri yok.</div>
            ) : (
              <SimpleLineChart
                title="Günlük Toplam (Para Birimi Bağımsız) - Sipariş / Adet / Kampanya Satırı"
                data={dailyAllCurrency}
                lines={[
                  { key: 'OrderCount', name: 'Sipariş' },
                  { key: 'Quantity', name: 'Adet' },
                  { key: 'CampaignLine', name: 'Kampanya Satırı' },
                ]}
              />
            )}

            {dailyByCurrency.length === 0 ? (
              <div style={{ padding: 8, border: '1px dashed #ccc', borderRadius: 8 }}>Grafik için veri yok.</div>
            ) : (
              dailyByCurrency.map(([cur, data]) => (
                <DailyChartsBlock key={cur} currencyKey={cur} data={data} />
              ))
            )}
          </div>

        </div>
      </div>
    </>
  )
}

export default componentWithContext(Index)
